import {
  IAnrokTaxConnectionSchema,
  IAvalaraTaxConnectionSchema,
  ILegalEntityResponseSchema,
  ITaxConnectionBaseSchema,
  ITaxConnectionSchema,
  ITaxwireTaxConnectionSchema,
  IVertexTaxConnectionSchema,
  TaxProviderTypeEnum,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import { arrayToObject } from '@monetize/utils/core';
import { useEffect, useState } from 'react';
import { parseMessageFromError } from '../../../../api/axios';
import { useGetById, useGetListData } from '../../../../api/queryUtils';
import { doTestTaxConnection } from '../../../../api/settingsService';
import {
  MBox,
  MGrid,
  MLink,
  MPageContainer,
  MSettingsPageHeader,
  MSkeleton,
  MText,
} from '../../../../components/Monetize';
import { TAX_PROVIDER_DISPLAY } from '../../../../constants/taxes';
import { useDocumentHead } from '../../../../services/documentHead';
import { useFlags } from '../../../../services/launchDarkly';
import { useToast } from '../../../../services/toast';
import { TaxCardEdit } from './components/TaxCardEdit';
import { TaxCardEmpty } from './components/TaxCardEmpty';
import { AnrokForm } from './components/anrok/AnrokForm';
import { AnrokTaxConnection } from './components/anrok/AnrokTaxConnection';
import { AvalaraForm } from './components/avalara/AvalaraForm';
import { AvalaraTaxConnection } from './components/avalara/AvalaraTaxConnection';
import { TaxwireTaxConnection } from './components/taxwire/TaxwireConnection';
import { TaxwireForm } from './components/taxwire/TaxwireForm';
import { VertexForm } from './components/vertex/VertexForm';
import { VertexTaxConnection } from './components/vertex/VertexTaxConnection';

const DOCUMENTATION_LINKS: Record<TaxProviderTypeEnum, string> = {
  [TaxProviderTypeEnum.ANROK]: 'https://docs.monetizenow.io/docs/anrok',
  [TaxProviderTypeEnum.AVALARA]: 'https://docs.monetizenow.io/docs/avalara',
  [TaxProviderTypeEnum.TAXWIRE]: 'https://docs.monetizenow.io/docs/taxwire',
  [TaxProviderTypeEnum.VERTEX]: 'https://docs.monetizenow.io/docs/vertex',
};

export const TaxConnections = () => {
  const { useTaxwireConfig, useVertexConfig } = useFlags();
  const [newConnectionType, setNewConnectionType] =
    useState<TaxProviderTypeEnum | null>(null);

  const { data: taxConnections, isLoading } = useGetListData<
    ITaxConnectionBaseSchema[],
    ITaxConnectionBaseSchema[]
  >('tax');

  const isAnrokConnectionEnabled = taxConnections?.find(
    ({ provider }) => provider === TaxProviderTypeEnum.ANROK,
  );
  const isAvalaraConnectionEnabled = taxConnections?.find(
    ({ provider }) => provider === TaxProviderTypeEnum.AVALARA,
  );
  const isTaxwireConnectionEnabled = taxConnections?.find(
    ({ provider }) => provider === TaxProviderTypeEnum.TAXWIRE,
  );
  const isVertexConnectionEnabled = taxConnections?.find(
    ({ provider }) => provider === TaxProviderTypeEnum.VERTEX,
  );

  const { data: anrokConnection, isLoading: isAnrokConnectionLoading } =
    useGetById<ITaxConnectionSchema>('tax', TaxProviderTypeEnum.ANROK, {
      enabled: !isLoading && !!isAnrokConnectionEnabled,
    });

  const { data: avalaraConnection, isLoading: isAvalaraConnectionLoading } =
    useGetById<ITaxConnectionSchema>('tax', TaxProviderTypeEnum.AVALARA, {
      enabled: !isLoading && !!isAvalaraConnectionEnabled,
    });

  const { data: taxwireConnection, isLoading: isTaxwireConnectionLoading } =
    useGetById<ITaxConnectionSchema>('tax', TaxProviderTypeEnum.TAXWIRE, {
      enabled: !isLoading && !!isTaxwireConnectionEnabled,
    });

  const { data: vertexConnection, isLoading: isVertexConnectionLoading } =
    useGetById<ITaxConnectionSchema>('tax', TaxProviderTypeEnum.VERTEX, {
      enabled: !isLoading && !!isVertexConnectionEnabled,
    });

  const { isLoading: legalEntitiesLoading, data: legalEntityData } =
    useGetListData<
      ILegalEntityResponseSchema,
      {
        legalEntitiesById: Record<string, ILegalEntityResponseSchema>;
        legalEntities: ILegalEntityResponseSchema[];
      }
    >(
      'legalEntities',
      {
        config: { ...DEFAULT_PAGER, rows: 50 },
      },
      {
        refetchOnWindowFocus: false,
        select: (data) => ({
          legalEntitiesById: arrayToObject(data.content, 'id'),
          legalEntities: data.content,
        }),
      },
    );

  const { addToast } = useToast();
  const { setDocTitle } = useDocumentHead();

  useEffect(() => {
    setDocTitle('Settings', 'Tax');
  }, [setDocTitle]);

  const verifyConnection = async (provider: TaxProviderTypeEnum) => {
    try {
      await doTestTaxConnection({
        provider,
        credentials: null,
      });
      const providerName = TAX_PROVIDER_DISPLAY[provider].name;

      addToast({
        summary: 'Connection Verified',
        detail: `Your connection to ${providerName} is configured correctly.`,
        severity: 'success',
      });
    } catch (ex) {
      const { summary, detail } = parseMessageFromError(ex);
      addToast({
        summary,
        detail,
        severity: 'error',
      });
    }
  };

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Tax" />

      <MBox maxW="600px">
        <MText>
          {!newConnectionType &&
            `Connect your tax processor and manage how you will handle taxation
          during billing.`}
          {newConnectionType === TaxProviderTypeEnum.ANROK &&
            `Connect to Anrok to sync taxation data to MonetizeNow.`}
          {newConnectionType === TaxProviderTypeEnum.AVALARA &&
            `Connect to Avalara to sync taxation data to MonetizeNow.`}
          {newConnectionType === TaxProviderTypeEnum.TAXWIRE &&
            `Connect to Taxwire to sync taxation data to MonetizeNow.`}
          {newConnectionType === TaxProviderTypeEnum.VERTEX &&
            `Connect to Vertex to sync taxation data to MonetizeNow.`}
        </MText>

        {newConnectionType && (
          <MText mb={2}>
            Check out our{' '}
            <MLink
              href={DOCUMENTATION_LINKS[newConnectionType]}
              target="_blank"
            >
              documentation
            </MLink>{' '}
            for more information on how to set up your tax connections.
          </MText>
        )}

        {isLoading ||
        legalEntitiesLoading ||
        (isAnrokConnectionEnabled && isAnrokConnectionLoading) ||
        (isAvalaraConnectionEnabled && isAvalaraConnectionLoading) ||
        (isTaxwireConnectionEnabled && isTaxwireConnectionLoading) ||
        (isVertexConnectionEnabled && isVertexConnectionLoading) ? (
          <MSkeleton height={36} w="full" />
        ) : (
          <MBox>
            {/* NO CONNECTION */}
            {avalaraConnection && !newConnectionType && (
              <MBox mb="4">
                <AvalaraTaxConnection
                  legalEntities={legalEntityData?.legalEntitiesById || {}}
                  connection={avalaraConnection as IAvalaraTaxConnectionSchema}
                  verifyConnection={verifyConnection}
                  isAvalaraConnectionLoading={isAvalaraConnectionLoading}
                />
              </MBox>
            )}

            {anrokConnection && !newConnectionType && (
              <AnrokTaxConnection
                legalEntities={legalEntityData?.legalEntitiesById || {}}
                connection={anrokConnection as IAnrokTaxConnectionSchema}
                verifyConnection={verifyConnection}
              />
            )}

            {taxwireConnection && !newConnectionType && (
              <TaxwireTaxConnection
                legalEntities={legalEntityData?.legalEntitiesById || {}}
                connection={taxwireConnection as ITaxwireTaxConnectionSchema}
                verifyConnection={verifyConnection}
              />
            )}

            {vertexConnection && !newConnectionType && (
              <MBox mb="4">
                <VertexTaxConnection
                  legalEntities={legalEntityData?.legalEntitiesById || {}}
                  connection={vertexConnection as IVertexTaxConnectionSchema}
                  verifyConnection={verifyConnection}
                />
              </MBox>
            )}

            {/* NEW AVALARA CONNECTION */}
            {newConnectionType === TaxProviderTypeEnum.AVALARA && (
              <TaxCardEdit type={TaxProviderTypeEnum.AVALARA}>
                <AvalaraForm handleCancel={() => setNewConnectionType(null)} />
              </TaxCardEdit>
            )}

            {/* NEW ANROK CONNECTION */}
            {newConnectionType === TaxProviderTypeEnum.ANROK && (
              <TaxCardEdit type={TaxProviderTypeEnum.ANROK}>
                <AnrokForm handleCancel={() => setNewConnectionType(null)} />
              </TaxCardEdit>
            )}

            {/* NEW TAXWIRE CONNECTION */}
            {newConnectionType === TaxProviderTypeEnum.TAXWIRE && (
              <TaxCardEdit type={TaxProviderTypeEnum.TAXWIRE}>
                <TaxwireForm handleCancel={() => setNewConnectionType(null)} />
              </TaxCardEdit>
            )}

            {/* NEW VERTEX CONNECTION */}
            {newConnectionType === TaxProviderTypeEnum.VERTEX && (
              <TaxCardEdit type={TaxProviderTypeEnum.VERTEX}>
                <VertexForm handleCancel={() => setNewConnectionType(null)} />
              </TaxCardEdit>
            )}

            {!newConnectionType && (
              <MGrid gridTemplateColumns="repeat(2, 1fr)" gap={6} mt={4}>
                {!avalaraConnection && (
                  <TaxCardEmpty
                    type={TaxProviderTypeEnum.AVALARA}
                    onClick={() =>
                      setNewConnectionType(TaxProviderTypeEnum.AVALARA)
                    }
                  />
                )}
                {!anrokConnection && (
                  <TaxCardEmpty
                    type={TaxProviderTypeEnum.ANROK}
                    onClick={() =>
                      setNewConnectionType(TaxProviderTypeEnum.ANROK)
                    }
                  />
                )}
                {!taxwireConnection && useTaxwireConfig && (
                  <TaxCardEmpty
                    type={TaxProviderTypeEnum.TAXWIRE}
                    onClick={() =>
                      setNewConnectionType(TaxProviderTypeEnum.TAXWIRE)
                    }
                  />
                )}
                {!vertexConnection && useVertexConfig && (
                  <TaxCardEmpty
                    type={TaxProviderTypeEnum.VERTEX}
                    onClick={() =>
                      setNewConnectionType(TaxProviderTypeEnum.VERTEX)
                    }
                  />
                )}
              </MGrid>
            )}
          </MBox>
        )}
      </MBox>
    </MPageContainer>
  );
};
